// ABOUTME: Timezone helpers for signup auto-detect and the settings picker.
// Detects the browser's IANA timezone via the standard `Intl` API (no deps)
// and exposes a curated list of common IANA zones grouped by continent for
// the `TimezoneSelect` component. Falls back to "UTC" when detection fails.

export interface TimezoneOption {
  /** IANA TZ identifier, e.g. "America/New_York". */
  value: string
  /** Human-readable label, e.g. "New York". */
  label: string
}

export interface TimezoneGroup {
  /** Continent / region label, e.g. "America". */
  region: string
  options: TimezoneOption[]
}

/**
 * Resolve the browser's IANA timezone, falling back to "UTC" on any failure
 * (very old browsers, locked-down environments, server-side renders).
 */
export function detectBrowserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (typeof tz === 'string' && tz.length > 0) {
      return tz
    }
  } catch {
    // Ignore — fall through to UTC.
  }
  return 'UTC'
}

/**
 * Curated list of ~50 IANA timezones grouped by continent. Covers every UTC
 * offset commonly seen in our SMB customer base. We intentionally avoid
 * `Intl.supportedValuesOf('timeZone')` because the full list is ~600 entries
 * and most are obscure aliases (e.g. `Asia/Yangon` vs `Asia/Rangoon`).
 */
export const TIMEZONE_GROUPS: readonly TimezoneGroup[] = [
  {
    region: 'America',
    options: [
      { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
      { value: 'America/Denver', label: 'Denver (MT)' },
      { value: 'America/Phoenix', label: 'Phoenix (MST, no DST)' },
      { value: 'America/Chicago', label: 'Chicago (CT)' },
      { value: 'America/New_York', label: 'New York (ET)' },
      { value: 'America/Anchorage', label: 'Anchorage (AKT)' },
      { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
      { value: 'America/Toronto', label: 'Toronto' },
      { value: 'America/Vancouver', label: 'Vancouver' },
      { value: 'America/Mexico_City', label: 'Mexico City' },
      { value: 'America/Sao_Paulo', label: 'São Paulo' },
      { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
      { value: 'America/Bogota', label: 'Bogotá' },
      { value: 'America/Lima', label: 'Lima' },
      { value: 'America/Santiago', label: 'Santiago' },
    ],
  },
  {
    region: 'Europe',
    options: [
      { value: 'Europe/London', label: 'London (GMT/BST)' },
      { value: 'Europe/Dublin', label: 'Dublin' },
      { value: 'Europe/Lisbon', label: 'Lisbon' },
      { value: 'Europe/Paris', label: 'Paris' },
      { value: 'Europe/Madrid', label: 'Madrid' },
      { value: 'Europe/Berlin', label: 'Berlin' },
      { value: 'Europe/Amsterdam', label: 'Amsterdam' },
      { value: 'Europe/Brussels', label: 'Brussels' },
      { value: 'Europe/Rome', label: 'Rome' },
      { value: 'Europe/Zurich', label: 'Zurich' },
      { value: 'Europe/Stockholm', label: 'Stockholm' },
      { value: 'Europe/Warsaw', label: 'Warsaw' },
      { value: 'Europe/Athens', label: 'Athens' },
      { value: 'Europe/Istanbul', label: 'Istanbul' },
      { value: 'Europe/Moscow', label: 'Moscow' },
    ],
  },
  {
    region: 'Asia',
    options: [
      { value: 'Asia/Dubai', label: 'Dubai' },
      { value: 'Asia/Karachi', label: 'Karachi' },
      { value: 'Asia/Kolkata', label: 'Kolkata (IST)' },
      { value: 'Asia/Bangkok', label: 'Bangkok' },
      { value: 'Asia/Jakarta', label: 'Jakarta' },
      { value: 'Asia/Singapore', label: 'Singapore' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
      { value: 'Asia/Shanghai', label: 'Shanghai' },
      { value: 'Asia/Taipei', label: 'Taipei' },
      { value: 'Asia/Seoul', label: 'Seoul' },
      { value: 'Asia/Tokyo', label: 'Tokyo' },
      { value: 'Asia/Manila', label: 'Manila' },
    ],
  },
  {
    region: 'Australia & Pacific',
    options: [
      { value: 'Australia/Perth', label: 'Perth' },
      { value: 'Australia/Adelaide', label: 'Adelaide' },
      { value: 'Australia/Brisbane', label: 'Brisbane' },
      { value: 'Australia/Sydney', label: 'Sydney' },
      { value: 'Pacific/Auckland', label: 'Auckland' },
      { value: 'Pacific/Fiji', label: 'Fiji' },
    ],
  },
  {
    region: 'Africa',
    options: [
      { value: 'Africa/Casablanca', label: 'Casablanca' },
      { value: 'Africa/Lagos', label: 'Lagos' },
      { value: 'Africa/Cairo', label: 'Cairo' },
      { value: 'Africa/Nairobi', label: 'Nairobi' },
      { value: 'Africa/Johannesburg', label: 'Johannesburg' },
    ],
  },
  {
    region: 'UTC',
    options: [{ value: 'UTC', label: 'UTC' }],
  },
] as const

/** Flat list of every TimezoneOption across all groups. */
export const ALL_TIMEZONE_OPTIONS: readonly TimezoneOption[] = TIMEZONE_GROUPS.flatMap(
  (g) => g.options
)

/**
 * True if `tz` is in the curated list (i.e. it can be rendered by the
 * `TimezoneSelect` without an extra "(detected)" entry being injected).
 */
export function isCuratedTimezone(tz: string): boolean {
  return ALL_TIMEZONE_OPTIONS.some((o) => o.value === tz)
}
